import { RunResponseType } from "../../storyV2/adapter/AdapterBase";
import { AdapterName } from "../../storyV2/adapter/AdapterRegistry";
import { CustomApiAdapter, CustomApiProps } from "../../storyV2/adapter/CustomApiAdapter";
import { CustomCodeAdapter, CustomCodeProps } from "../../storyV2/adapter/CustomCodeAdapter";
import { StoryNodeDisplayType } from "../../storyV2/domain/StoryNodeDisplayType";
import isBlank from "@sedan-utils/is-blank";
import { StoryUtil } from "../domain/StoryUtil";
import { uploadFileTypes } from "../../project/domain/StudioProjectAttributesV3NodeData";
import { DefaultBehaviorService } from "../service/DefaultBehaviorService";
import { CustomApiTransformUtil } from "../service/transfromService/util/CustomApiTransformUtil";
import { StoryNodeType } from "../domain/StoryNodeType";
export class AdapterUtil {
    static customFuncType(dest) {
        if (dest === undefined) {
            return undefined;
        }
        const type = dest.type?.toLowerCase();
        switch (type) {
            case "customapi":
                return "customApi";
            case "customcode":
                return "customCode";
            default:
                return "customFunction";
        }
    }
    static parseCustomCodeProps(dest) {
        const props = new CustomCodeProps();
        props.htmlStr = dest?.codeData?.htmlStr || "";
        props.cssStr = dest?.codeData?.cssStr || "";
        props.jsStr = dest?.codeData?.jsStr || "";
        return props;
    }
    static parseCustomApiProps(dest) {
        const props = new CustomApiProps();
        props.url = dest?.apiData?.requestData?.url;
        props.method = dest?.apiData?.requestData?.method;
        props.headerList = dest?.apiData?.requestData?.headerList;
        props.bodyParam = dest?.apiData?.requestData?.bodyParam;
        return props;
    }
    static isFuncEdge(enterText) {
        const selectApiReg = /^\//;
        return selectApiReg.test(enterText);
    }
    static isNeedGeneratePromptEdge(edge, sourceNodes) {
        const enterText = edge.options.actionData.enterText;
        const identifierValue = edge.options.actionData.identifierValue;
        const hasFileNode = sourceNodes.some(node => CustomApiTransformUtil.isUploadFileType(node.options.displayType));
        return this.isFuncEdge(enterText) === false && identifierValue === "" && hasFileNode === false;
    }
    /*
      There are 3 scenarios when runnig an edge
      1. a defined function is selected by the user
      2. (TBD) a function is selected by a smart domain mapping mechanism
      3. when there is no function selected, a default behavior is invoked
    */
    static async getAdapter(story, edge, adapterRegistry, relatedEdges) {
        const enterText = edge.options.actionData.enterText;
        const funcId = edge.options.actionData.funcOriginalData.id;
        const funcValue = edge.options.actionData.funcOriginalData.value;
        // special situation
        if (isBlank(edge.options.actionData.blueJson) === false) {
            const adapter = await adapterRegistry.get(edge.options.actionData.lineType, undefined, undefined, true);
            return { adapter: adapter, actionData: edge.options.actionData };
        }
        // 1. when a defined function is selected by the user
        if (AdapterUtil.isFuncEdge(enterText)) {
            const adapter = await adapterRegistry.get(enterText, funcId, funcValue);
            if (adapter == undefined) {
                const relatedEdge = relatedEdges.pop();
                if (relatedEdge != undefined) {
                    return AdapterUtil.getAdapter(story, relatedEdge, adapterRegistry, relatedEdges);
                }
                else {
                    return { adapter: adapter, actionData: edge.options.actionData };
                }
            }
            else {
                return { adapter: adapter, actionData: edge.options.actionData };
            }
        }
        // 3. when there is no function selected, a default behavior is invoked
        const edgeSource = StoryUtil.findEdgeSoure(story, edge);
        const edgeSources = StoryUtil.findEdgeSoures(story, edge);
        const defaultBehavior = new DefaultBehaviorService(adapterRegistry, edgeSource, edgeSources);
        const adapter = await defaultBehavior.getAdapter();
        if (adapter) {
            return { adapter: adapter, actionData: edge.options.actionData };
        }
        if (edgeSource?.options.type === StoryNodeType.CUSTOMGROUP) {
            const nodes = edgeSource.findChildren();
            const filteredNodes = nodes.filter(x => {
                if (uploadFileTypes.includes(x.options.displayType || "")) {
                    return true;
                }
                return false;
            });
            if (!isBlank(filteredNodes)) {
                const adapter = await adapterRegistry.get(AdapterName.addDataSource);
                return { adapter: adapter, actionData: edge.options.actionData };
            }
        }
        // legacy logic to support v2 projects
        return this.getAdapterLegacyMultiInput(story, edge, adapterRegistry, relatedEdges);
    }
    // To be deprecated
    static async getAdapterLegacyMultiInput(story, edge, adapterRegistry, relatedEdges) {
        const adapter = await adapterRegistry.get(edge.options.actionData.lineType, undefined, undefined, !isBlank(edge.options.actionData.blueJson));
        if (adapter == undefined || isBlank(edge.options.actionData.identifierValue)) {
            const relatedEdge = relatedEdges.pop();
            if (relatedEdge != undefined) {
                return AdapterUtil.getAdapter(story, relatedEdge, adapterRegistry, relatedEdges);
            }
            else {
                return { adapter: adapter, actionData: edge.options.actionData };
            }
        }
        else {
            return { adapter: adapter, actionData: edge.options.actionData };
        }
    }
    static typeTransform(runResponseType, actionData, adapter) {
        switch (runResponseType) {
            case RunResponseType.STOCK:
                return StoryNodeDisplayType.STOCKINFO;
            case RunResponseType.IMAGE || RunResponseType.IMAGE_GROUP || RunResponseType.TUTORIAL:
                if (actionData.enterText === "/Image search") {
                    return StoryNodeDisplayType.IMAGESEARCH;
                }
                return StoryNodeDisplayType.IMAGEGEN;
            case RunResponseType.STRING_GROUP || RunResponseType.STREAM:
                return StoryNodeDisplayType.TEXT;
            case RunResponseType.TEXT:
                if (adapter instanceof CustomCodeAdapter) {
                    return StoryNodeDisplayType.CUSTOMJSFUNCTION;
                }
                if (adapter instanceof CustomApiAdapter) {
                    return StoryNodeDisplayType.CUSTOMAPIFUNCTION;
                }
                return StoryNodeDisplayType.TEXT;
            case RunResponseType.AMAZONSHOPPING:
                return StoryNodeDisplayType.AMAZONSHOPPING;
            case RunResponseType.ASK_BRAIN:
                return StoryNodeDisplayType.ASKBRAIN;
            case RunResponseType.FOOD:
                return StoryNodeDisplayType.SHOWFOODINTERFACE;
            case RunResponseType.JDSHOPPING:
                return StoryNodeDisplayType.JDSHOPPING;
            case RunResponseType.MAP:
                return StoryNodeDisplayType.MAP;
            case RunResponseType.WEEEGRPCERIES:
                return StoryNodeDisplayType.WEEESHOPPING;
            case RunResponseType.WEEESHOPPING:
                return StoryNodeDisplayType.WEEESHOPPING;
            case RunResponseType.YOUTUBE_VIDEOS:
                return StoryNodeDisplayType.VIDEO;
            case RunResponseType.PROTEIN:
                return StoryNodeDisplayType.CUSTOMJSFUNCTION;
            case RunResponseType.CHAT_INTERFACE:
                return StoryNodeDisplayType.CHAT_INTERFACE;
            case RunResponseType.BLUEPRINT:
                return StoryNodeDisplayType.BLUEPRINT;
            case RunResponseType.INTERVIEW_BOT:
                return StoryNodeDisplayType.INTERVIEW_BOT;
        }
        return StoryNodeDisplayType.TEXT;
    }
    static allImgAdapter() {
        return [AdapterName.dallE2, AdapterName.imageSearch];
    }
    /// node转换时，会调用这个方法，判断target node是否可能是image
    /// 这样target node的displayType会第一时间变成img，这样img node能正确显示loading
    static outputMaybeImage(edgeName) {
        const lowerCaseEdgeName = edgeName.toLowerCase();
        /// hardcode的确定输出image的边
        const lowerCaseImgAdapter = this.allImgAdapter().map(e => e.toLowerCase());
        if (lowerCaseImgAdapter.includes(lowerCaseEdgeName)) {
            return true;
        }
        /// 已知gloal存在的可能输出image的边
        const exsitGloalImgEdges = [
            "/Animation Style",
            "/Animation Style (file upload)",
            "/Generate an Image",
            "/Generate an Image (file upload)",
            "/Generate a Photo",
            "/Generate a Photo (file upload)",
            "/Image Search",
            "/Image Search(Old)",
            "/Image Search (file upload)",
            "/Cybernetic Painting Style",
            "/Cybernetic Painting Style (file upload)",
            "/Cute Emoji Style",
            "/Cute Emoji Style (file upload)",
            "/Midjourney",
        ];
        const lowerExsitGloalEdges = exsitGloalImgEdges.map(e => e.toLowerCase());
        if (lowerExsitGloalEdges.includes(lowerCaseEdgeName)) {
            return true;
        }
        return false;
    }
}
